import { useSortBy } from 'react-instantsearch-core';
import { useIntl } from 'react-intl';

import type { WatchCollectionFilterProps } from '@/components/ListingFilters/components/WatchCollectionFilter';
import { WatchCollectionFilter } from '@/components/ListingFilters/components/WatchCollectionFilter';
import { useListingSortBy } from '@/hooks/useListingSortBy';
import { useListingState } from '@/hooks/useListingState';

import { FilterAccordion } from '../FilterAccordion/FilterAccordion';
import { PriceRangeFilter } from '../PriceRangeFilter';
import { SortByFilterWrapper } from '../SortByFilter';
import { SwatchFilterWrapper } from '../SwatchFilter';
import { TextualFilterOptions } from '../TextualFilterOptions';

import { filtersModalMessages } from './messages';

export type FiltersProps = Pick<
  WatchCollectionFilterProps,
  'collectionData'
> & {
  showCollectionFilter?: boolean;
  showMaterialsFilter?: boolean;
};

export const Filters = ({
  collectionData,
  showCollectionFilter = false,
  showMaterialsFilter = false,
}: FiltersProps) => {
  const { formatMessage } = useIntl();
  const { listingType } = useListingState();

  const sortItems: Array<{ label: string; value: string }> = useListingSortBy();
  const { currentRefinement: sortRefinement, options: sortOptions } = useSortBy(
    {
      items: sortItems,
    },
  );
  const sortOption = sortOptions.find(
    (option) => option.value === sortRefinement,
  );
  const selectedSortOption = sortOption ? sortOption.label : '';

  switch (listingType) {
    case 'WATCH':
      return (
        <>
          <FilterAccordion
            summary={`${formatMessage(
              filtersModalMessages.sortBy,
            )} ${selectedSortOption}`}
          >
            <SortByFilterWrapper />
          </FilterAccordion>
          {showCollectionFilter && (
            <FilterAccordion
              attribute='collection'
              summary={formatMessage(filtersModalMessages.collection)}
            >
              <WatchCollectionFilter
                collectionData={collectionData}
                isLoading={false}
              />
            </FilterAccordion>
          )}

          <FilterAccordion
            attribute='type'
            summary={formatMessage(filtersModalMessages.type)}
          >
            <TextualFilterOptions attribute='type' />
          </FilterAccordion>

          <FilterAccordion
            attribute='size'
            summary={formatMessage(filtersModalMessages.diameter)}
          >
            <TextualFilterOptions attribute='size' />
          </FilterAccordion>

          <FilterAccordion
            attribute='case'
            summary={formatMessage(filtersModalMessages.caseMaterial)}
          >
            <SwatchFilterWrapper attribute='case' />
          </FilterAccordion>

          <FilterAccordion
            attribute='dial_color'
            summary={formatMessage(filtersModalMessages.dialColor)}
          >
            <SwatchFilterWrapper attribute='dial_color' />
          </FilterAccordion>

          <FilterAccordion
            attribute='price'
            summary={formatMessage(filtersModalMessages.price)}
          >
            <PriceRangeFilter />
          </FilterAccordion>
        </>
      );
    case 'EYEWEAR':
      return (
        <>
          <FilterAccordion
            summary={`${formatMessage(
              filtersModalMessages.sortBy,
            )} ${selectedSortOption}`}
          >
            <SortByFilterWrapper />
          </FilterAccordion>

          <FilterAccordion
            attribute='frame_material'
            summary={formatMessage(filtersModalMessages.frameMaterial)}
          >
            <TextualFilterOptions attribute='frame_material' />
          </FilterAccordion>

          <FilterAccordion
            attribute='lens_color'
            summary={formatMessage(filtersModalMessages.lensColor)}
          >
            <SwatchFilterWrapper attribute='lens_color' />
          </FilterAccordion>

          <FilterAccordion
            attribute='price'
            summary={formatMessage(filtersModalMessages.price)}
          >
            <PriceRangeFilter />
          </FilterAccordion>
        </>
      );
    case 'STRAP':
      return (
        <>
          <FilterAccordion
            summary={`${formatMessage(
              filtersModalMessages.sortBy,
            )} ${selectedSortOption}`}
          >
            <SortByFilterWrapper />
          </FilterAccordion>
          <FilterAccordion
            attribute='distance_between_horns_en'
            summary={formatMessage(filtersModalMessages.lug)}
          >
            <TextualFilterOptions attribute='distance_between_horns_en' />
          </FilterAccordion>
          <FilterAccordion
            attribute='lug_type'
            summary={formatMessage(filtersModalMessages.lugType)}
          >
            <TextualFilterOptions attribute='lug_type' />
          </FilterAccordion>
          {showMaterialsFilter && (
            <FilterAccordion
              attribute='material'
              summary={formatMessage(filtersModalMessages.material)}
            >
              <TextualFilterOptions attribute='material' />
            </FilterAccordion>
          )}

          <FilterAccordion
            attribute='color'
            summary={formatMessage(filtersModalMessages.color)}
          >
            <SwatchFilterWrapper attribute='color' />
          </FilterAccordion>

          <FilterAccordion
            attribute='buckle_type'
            summary={formatMessage(filtersModalMessages.buckle)}
          >
            <TextualFilterOptions attribute='buckle_type' />
          </FilterAccordion>
        </>
      );
    case 'STORE':
      return (
        <FilterAccordion
          attribute='type'
          summary={formatMessage(filtersModalMessages.storeType)}
        >
          <TextualFilterOptions attribute='type' listing={listingType} />
        </FilterAccordion>
      );
    default:
      return null;
  }
};
